import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Icon from '../components/ui/Icon'
import AuthGuard from '../components/AuthGuard'
import apiService from '../services/api'

const CommunityMembers = () => {
  return (
    <AuthGuard 
      requireAuth={true}
      title="কমিউনিটি সদস্য দেখতে লগিন করুন"
      description="কমিউনিটির সদস্যদের তথ্য ও পরিসংখ্যান দেখতে অ্যাকাউন্টে লগিন করুন।"
    >
      {({ user, logout, openAuth }) => <CommunityMembersContent user={user} logout={logout} openAuth={openAuth} />}
    </AuthGuard>
  )
}

const CommunityMembersContent = ({ user, logout, openAuth }) => {
  const { communityId } = useParams()
  const navigate = useNavigate()
  const [communityData, setCommunityData] = useState(null)
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('members')
  const [callHistories, setCallHistories] = useState({})
  
  // Add Member Modal State
  const [showAddMemberModal, setShowAddMemberModal] = useState(false)
  const [memberPhoneNumber, setMemberPhoneNumber] = useState('')
  const [addMemberLoading, setAddMemberLoading] = useState(false)
  const [isCreator, setIsCreator] = useState(false)
  const [dialingId, setDialingId] = useState(null)

  useEffect(() => {
    if (communityId) {
      loadCommunityData()
    }
  }, [communityId])

  const loadCommunityData = async () => {
    try {
      setLoading(true)
      const response = await apiService.getCommunityMembers(communityId)
      setCommunityData({
        name: response.community_name,
        members: response.members
      })
      const membersList = response.members || []
      setMembers(membersList)
      
      // Check if current user is the creator. Matched by id: the list is
      // moving to masked numbers, which can no longer be compared.
      const currentUser = membersList.find(member => member.id === user?.id)
      setIsCreator(currentUser?.is_creator || false)
      
      // Load call histories for all members
      await loadCallHistories(membersList)
    } catch (err) {
      console.error('Failed to load community members:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const loadCallHistories = async (membersList) => {
    const histories = {}
    
    // Load call history for each member in parallel
    await Promise.all(membersList.map(async (member) => {
      try {
        histories[member.id] = await apiService.getCommunityMemberCallHistory(communityId, member.id)
      } catch (err) {
        console.error(`Failed to load call history for member ${member.id}:`, err)
        histories[member.id] = { call_history: [], total_calls_today: 0 }
      }
    }))
    
    setCallHistories(histories)
  }

  // The page shows only masked numbers; the full one is fetched for the single
  // member being called, at the moment Call is tapped.
  const makeCall = async (member) => {
    if (dialingId) return
    setDialingId(member.id)
    try {
      const { phone_number } = await apiService.getCommunityMemberPhone(communityId, member.id)
      window.open(`tel:${phone_number}`, '_self')
    } catch {
      // The list still carries the full number for the installed Flutter app.
      // Fall back to it, so a failed lookup never stops a wake-up call.
      if (member.phone_number) window.open(`tel:${member.phone_number}`, '_self')
      else alert('নম্বর আনা যায়নি। আবার চেষ্টা করুন।')
    } finally {
      setDialingId(null)
    }
  }

  const handleAddMember = async (e) => {
    e.preventDefault()
    if (!memberPhoneNumber.trim()) return

    try {
      setAddMemberLoading(true)
      const response = await apiService.addCommunityMember(communityId, memberPhoneNumber.trim())
      
      setMemberPhoneNumber('')
      setShowAddMemberModal(false)
      alert(response.message || 'অনুরোধ পাঠানো হয়েছে!')
    } catch (err) {
      console.error('Failed to add member:', err)
      alert('Failed to send request: ' + err.message)
    } finally {
      setAddMemberLoading(false)
    }
  }

  const getLeaderboard = () => {
    return members
      .sort((a, b) => {
        // Sort by recent wake-up count, then by total points
        const aWakeups = a.recent_stats?.days_woken_last_30 || 0
        const bWakeups = b.recent_stats?.days_woken_last_30 || 0
        if (bWakeups !== aWakeups) return bWakeups - aWakeups
        
        return (b.recent_stats?.total_points || 0) - (a.recent_stats?.total_points || 0)
      })
      .map((member, index) => ({
        ...member,
        rank: index + 1
      }))
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading community members...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <strong>Error:</strong> {error}
            </div>
            <Button onClick={() => navigate('/communities')}>
              Back to Communities
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const leaderboard = getLeaderboard()

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} onLogout={logout} onAuth={openAuth} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/communities')}
              >
                <Icon name="ArrowLeft" className="w-4 h-4 mr-1" />
                ফিরে যান
              </Button>
              <div>
                <h1 className="text-3xl font-bold">{communityData?.name}</h1>
                <p className="text-muted-foreground">{members.length} জন সদস্য</p>
              </div>
            </div>
            
            {/* Action Buttons - Below header for mobile responsiveness */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Add Member Button - Only show for creators */}
              {isCreator && (
                <Button
                  variant="outline"
                  onClick={() => setShowAddMemberModal(true)}
                  className="w-full sm:w-auto"
                >
                  <Icon name="Plus" className="w-4 h-4 mr-2" />
                  সদস্য যোগ করুন
                </Button>
              )}
              
              {/* Wake Up Members Button */}
              <Button
                onClick={() => navigate('/community-wake-up')}
                className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
              >
                <Icon name="Bell" className="w-4 h-4 mr-2" />
                সদস্যদের জাগান
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 bg-muted rounded-lg p-1 mb-6">
            <button
              onClick={() => setActiveTab('members')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'members'
                  ? 'bg-white text-foreground shadow'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Users" className="w-4 h-4 mr-2 inline" />
              সদস্যরা
            </button>
            <button
              onClick={() => setActiveTab('leaderboard')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'leaderboard'
                  ? 'bg-white text-foreground shadow'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Trophy" className="w-4 h-4 mr-2 inline" />
              লিডারবোর্ড
            </button>
          </div>

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {members.map((member) => (
                <Card key={member.id} className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">
                          {member.name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold">{member.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {member.phone_display ?? member.phone_number}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {member.is_creator && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                          Admin
                        </span>
                      )}
                      {!member.woke_up_today && member.is_available && (
                        <Button
                          size="sm"
                          onClick={() => makeCall(member)}
                          disabled={dialingId === member.id}
                          className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1"
                        >
                          <Icon name="Phone" className="w-3 h-3 mr-1" />
                          Call
                        </Button>
                      )}
                      <div className={`w-3 h-3 rounded-full ${
                        member.woke_up_today 
                          ? 'bg-green-500' 
                          : member.is_available 
                            ? 'bg-orange-500' 
                            : 'bg-gray-400'
                      }`} />
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">আজকে:</span>
                      <span className={member.woke_up_today ? 'text-green-600 font-medium' : 'text-gray-500'}>
                        {member.woke_up_today ? 'জেগেছে ✓' : 'এখনো জাগেনি'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">গত ৩০ দিনে:</span>
                      <span className="font-medium">
                        {member.recent_stats?.days_woken_last_30 || 0} দিন জেগেছে
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">আজকে কল পেয়েছে:</span>
                      <span className="font-medium text-blue-600">
                        {callHistories[member.id]?.total_calls_today || 0} বার
                      </span>
                    </div>
                    
                    {/* Call History */}
                    {callHistories[member.id]?.call_history?.length > 0 && (
                      <div className="mt-3 pt-2 border-t">
                        <span className="text-muted-foreground text-xs">আজকের কল হিস্ট্রি:</span>
                        <div className="mt-1 space-y-1">
                          {callHistories[member.id].call_history.slice(0, 3).map((call, index) => (
                            <div key={index} className="flex justify-between text-xs">
                              <span className={call.is_own_call ? 'text-primary font-medium' : 'text-gray-600'}>
                                {call.is_own_call ? 'আপনি' : call.caller_name} ({call.call_time})
                              </span>
                              <span className={`px-1 rounded text-xs ${
                                call.status === 'received' ? 'bg-green-100 text-green-700' :
                                call.status === 'busy' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {call.status === 'received' ? 'উত্তর দিয়েছে' :
                                 call.status === 'busy' ? 'ব্যস্ত' :
                                 call.status === 'not_received' ? 'উত্তর দেয়নি' :
                                 call.status === 'hang_up' ? 'কেটে দিয়েছে' : call.status}
                              </span>
                            </div>
                          ))}
                          {callHistories[member.id].call_history.length > 3 && (
                            <div className="text-xs text-muted-foreground">
                              আরো {callHistories[member.id].call_history.length - 3}টি কল...
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                </Card>
              ))}
            </div>
          )}

          {/* Leaderboard Tab */}
          {activeTab === 'leaderboard' && (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2">গত ৩০ দিনের পারফরম্যান্স</h2>
                <p className="text-muted-foreground text-sm">
                  জাগানোর দিনের সংখ্যা অনুযায়ী র‌্যাংকিং
                </p>
              </div>
              
              <div className="space-y-4">
                {leaderboard.map((member, index) => (
                  <Card key={member.id} className="p-4">
                    <div className="flex items-center space-x-4">
                      {/* Rank */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                        index === 0 ? 'bg-yellow-500' :
                        index === 1 ? 'bg-gray-400' :
                        index === 2 ? 'bg-orange-600' :
                        'bg-gray-300 text-gray-700'
                      }`}>
                        {index === 0 ? '🏆' : 
                         index === 1 ? '🥈' :
                         index === 2 ? '🥉' : 
                         member.rank}
                      </div>
                      
                      {/* Member Info */}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold flex items-center space-x-2">
                              <span>{member.name}</span>
                              {member.is_creator && (
                                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                                  Admin
                                </span>
                              )}
                              {member.woke_up_today && (
                                <span className="text-green-600 text-sm">● আজ জেগেছে</span>
                              )}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {member.phone_display ?? member.phone_number}
                            </p>
                          </div>
                          
                          <div className="text-right">
                            <div className="text-lg font-bold text-primary">
                              {member.recent_stats?.days_woken_last_30 || 0}
                            </div>
                            <div className="text-xs text-muted-foreground">দিন জেগেছে</div>
                          </div>
                        </div>
                        
                        <div className="mt-2 flex space-x-4 text-sm text-muted-foreground">
                          <span>সাহায্য: {member.recent_stats?.people_helped_last_30 || 0}জন</span>
                          <span>পয়েন্ট: {member.recent_stats?.total_points || 0}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              
              {members.length === 0 && (
                <Card className="text-center py-12">
                  <Icon name="Users" className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">কোন সদস্য নেই</h3>
                  <p className="text-muted-foreground">
                    এই কমিউনিটিতে এখনো কোন সদস্য নেই।
                  </p>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">সদস্য যোগ করুন</h3>
              <button
                onClick={() => setShowAddMemberModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddMember}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  ফোন নাম্বার
                </label>
                <input
                  type="tel"
                  value={memberPhoneNumber}
                  onChange={(e) => setMemberPhoneNumber(e.target.value)}
                  placeholder="+8801812345678 or 01812345678"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  pattern="[+]?[0-9]{10,15}"
                  minLength={10}
                  maxLength={15}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  দেশীয় বা আন্তর্জাতিক নাম্বার (দেশের কোড সহ বা ছাড়া)
                </p>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddMemberModal(false)}
                  className="flex-1"
                >
                  বাতিল
                </Button>
                <Button
                  type="submit"
                  disabled={addMemberLoading || !memberPhoneNumber.trim()}
                  className="flex-1"
                >
                  {addMemberLoading ? 'পাঠানো হচ্ছে...' : 'রিকোয়েস্ট পাঠান'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      <Footer />
    </div>
  )
}

export default CommunityMembers